import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { queryKeys } from "@/lib/queryKeys";
import { invalidateInventoryQueries } from "@/lib/mutationHelpers";
import { deleteGuestGuideKnowledge } from "@/lib/guestGuideHospitable";

export const useInventoryItems = (propertyId: string | undefined) => {
  return useQuery({
    queryKey: ["inventory", propertyId],
    queryFn: async () => {
      if (!propertyId) return [];
      const { data, error } = await supabase
        .from("inventory_items")
        .select("*")
        .eq("property_id", propertyId)
        .order("item_name", { ascending: true });

      if (error) throw error;
      return data;
    },
    enabled: !!propertyId,
    staleTime: 15_000, // Phones and other accounts change this data; recheck soon
    refetchOnMount: true,
    placeholderData: [], // Show empty array while loading
  });
};

export type QuantityType = 'units' | 'fullness';

/** Slice 15 Level chips — Full · ¾ · ½ · ¼ · Empty */
export const FULLNESS_OPTIONS = [
  { value: 1.0, label: 'Full' },
  { value: 0.75, label: '¾' },
  { value: 0.5, label: '½' },
  { value: 0.25, label: '¼' },
  { value: 0.0, label: 'Empty' },
] as const;

export const FULLNESS_THRESHOLD_OPTIONS = [
  { value: 0.75, label: '≤ ¾' },
  { value: 0.5, label: '≤ ½' },
  { value: 0.25, label: '≤ ¼' },
  { value: 0.0, label: 'Empty' },
] as const;

export const getFullnessLabel = (value: number | null): string => {
  if (value === null) return 'Unknown';
  const option = FULLNESS_OPTIONS.find(o => o.value === value);
  return option?.label || `${Math.round(value * 100)}%`;
};

export const useCreateInventoryItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      propertyId,
      itemName,
      thresholdCount,
      currentCount,
      photoUrl,
      roomId,
      quantityType,
      currentValue,
      thresholdValue,
      brand,
      modelNumber,
      serialNumber,
      inventoryScope,
    }: {
      propertyId: string;
      itemName: string;
      thresholdCount: number | null;
      currentCount: number;
      photoUrl?: string | null;
      roomId?: string | null;
      quantityType?: QuantityType;
      currentValue?: number | null;
      thresholdValue?: number | null;
      brand?: string | null;
      modelNumber?: string | null;
      serialNumber?: string | null;
      inventoryScope?: "unit" | "guest_amenities" | "supply_closet";
    }) => {
      const { data, error } = await supabase
        .from("inventory_items")
        .insert({
          property_id: propertyId,
          item_name: itemName,
          threshold_count: thresholdCount ?? null,
          current_count: currentCount,
          in_stock_qty: Math.max(0, currentCount),
          ordered_qty: 0,
          photo_url: photoUrl || null,
          room_id: roomId || null,
          quantity_type: quantityType || 'units',
          current_value: currentValue ?? null,
          threshold_value: thresholdValue ?? null,
          brand: brand ?? null,
          model_number: modelNumber ?? null,
          serial_number: serialNumber ?? null,
          inventory_scope: inventoryScope ?? "supply_closet",
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data, variables) => {
      const mergeNewItem = (old: unknown[] | undefined) => {
        const list = old ?? [];
        if (list.some((i: { id?: string }) => i.id === data?.id)) return list;
        return [...list, data].sort((a: { item_name?: string }, b: { item_name?: string }) =>
          (a.item_name ?? "").localeCompare(b.item_name ?? "")
        );
      };
      // Update owner inventory list cache
      queryClient.setQueryData(queryKeys.inventory.byProperty(variables.propertyId), mergeNewItem);
      // Update cleaner inventory list cache so new item appears in purchase dropdown without refresh
      queryClient.setQueryData(["inventory-cleaner", variables.propertyId], mergeNewItem);
      queryClient.invalidateQueries({ queryKey: ["property-dashboard", variables.propertyId] });
      queryClient.invalidateQueries({ queryKey: ["property-status-badges"] });
    },
  });
};

export const useUpdateInventoryItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      propertyId,
      itemName,
      thresholdCount,
      currentCount,
      photoUrl,
      roomId,
      quantityType,
      currentValue,
      thresholdValue,
      brand,
      modelNumber,
      serialNumber,
      inventoryScope,
    }: {
      id: string;
      propertyId: string;
      itemName: string;
      thresholdCount: number | null;
      currentCount: number;
      photoUrl?: string | null;
      roomId?: string | null;
      quantityType?: QuantityType;
      currentValue?: number | null;
      thresholdValue?: number | null;
      brand?: string | null;
      modelNumber?: string | null;
      serialNumber?: string | null;
      inventoryScope?: "unit" | "guest_amenities" | "supply_closet";
    }) => {
      const payload: Record<string, unknown> = {
        item_name: itemName,
        threshold_count: thresholdCount ?? null,
        current_count: currentCount,
        in_stock_qty: Math.max(0, currentCount),
        photo_url: photoUrl,
        room_id: roomId,
        quantity_type: quantityType || "units",
        current_value: currentValue ?? null,
        threshold_value: thresholdValue ?? null,
        brand: brand ?? null,
        model_number: modelNumber ?? null,
        serial_number: serialNumber ?? null,
      };
      if (inventoryScope !== undefined) {
        payload.inventory_scope = inventoryScope;
      }
      const { data, error } = await supabase.from("inventory_items").update(payload).eq("id", id).select().single();

      if (error) throw error;
      return data;
    },
    onSuccess: async (_, variables) => {
      await invalidateInventoryQueries(queryClient, variables.propertyId);
      queryClient.invalidateQueries({ queryKey: ["property-status-badges"] });
      queryClient.invalidateQueries({ queryKey: ["needs-attention"] });
    },
  });
};


export const useUpdateInventoryItemPhoto = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      propertyId,
      photoUrl,
    }: {
      id: string;
      propertyId: string;
      photoUrl: string | null;
    }) => {
      const { data, error } = await supabase
        .from("inventory_items")
        .update({ photo_url: photoUrl })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: async (_, variables) => {
      await invalidateInventoryQueries(queryClient, variables.propertyId);
      queryClient.invalidateQueries({ queryKey: ["property-status-badges"] });
    },
  });
};

export const useDeleteInventoryItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, propertyId }: { id: string; propertyId: string }) => {
      const { data: existing } = await supabase
        .from("inventory_items")
        .select("hospitable_knowledge_item_id")
        .eq("id", id)
        .maybeSingle();
      await deleteGuestGuideKnowledge({
        propertyId,
        knowledgeItemId: existing?.hospitable_knowledge_item_id,
      });
      const { error } = await supabase.from("inventory_items").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: async (_, variables) => {
      await invalidateInventoryQueries(queryClient, variables.propertyId);
      queryClient.invalidateQueries({ queryKey: ["property-status-badges"] });
    },
  });
};

/** Increment ordered_qty when a purchase is logged (cleaner flow). */
export const useIncrementOrderedQty = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      itemId,
      propertyId,
      quantity,
    }: { itemId: string; propertyId: string; quantity: number }) => {
      const qty = Math.max(1, Math.floor(quantity));
      const { data: row } = await supabase
        .from("inventory_items")
        .select("ordered_qty")
        .eq("id", itemId)
        .eq("property_id", propertyId)
        .single();
      const current = (row as { ordered_qty?: number } | null)?.ordered_qty ?? 0;
      const { data, error } = await supabase
        .from("inventory_items")
        .update({ ordered_qty: current + qty })
        .eq("id", itemId)
        .eq("property_id", propertyId)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      invalidateInventoryQueries(queryClient, variables.propertyId);
      queryClient.invalidateQueries({ queryKey: ["property-status-badges"] });
      queryClient.invalidateQueries({ queryKey: ["needs-attention"] });
    },
  });
};

/** Mark received: move qty from Ordered (not arrived) → In stock. */
export const useMarkInventoryReceived = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      itemId,
      propertyId,
      qtyReceived,
    }: { itemId: string; propertyId: string; qtyReceived: number }) => {
      const qty = Math.max(1, Math.floor(qtyReceived));
      const { data: row } = await supabase
        .from("inventory_items")
        .select("in_stock_qty, ordered_qty")
        .eq("id", itemId)
        .eq("property_id", propertyId)
        .single();
      const inStock = (row as { in_stock_qty?: number } | null)?.in_stock_qty ?? 0;
      const ordered = (row as { ordered_qty?: number } | null)?.ordered_qty ?? 0;
      const move = Math.min(qty, ordered);
      if (move <= 0) throw new Error("No quantity ordered to receive");
      const { data, error } = await supabase
        .from("inventory_items")
        .update({
          in_stock_qty: inStock + move,
          ordered_qty: Math.max(0, ordered - move),
        })
        .eq("id", itemId)
        .eq("property_id", propertyId)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      invalidateInventoryQueries(queryClient, variables.propertyId);
      queryClient.invalidateQueries({ queryKey: ["property-status-badges"] });
      queryClient.invalidateQueries({ queryKey: ["needs-attention"] });
    },
  });
};
